import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  where,
  query,
  orderBy,
  limit,
  startAfter,
  writeBatch,
  increment,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';

const PAGE_SIZE = 30;

export const addMessage = createAsyncThunk('message/addMessage', async ({ chatId, data, recipientUid }) => {
  // Re-create recipient's chat entry BEFORE updating the chatlog so that when onSnapshot
  // fires on the recipient's side, fetchContacts already finds the entry in Firestore.
  if (recipientUid) {
    const recipientChatRef = doc(db, 'users', recipientUid, 'chats', data.uid);
    const recipientChatSnap = await getDoc(recipientChatRef);
    if (!recipientChatSnap.exists()) {
      const chatlogSnap = await getDoc(doc(db, 'chatlogs', chatId));
      const chatlogData = chatlogSnap.data();
      const senderMember = chatlogData?.members?.find((m) => m.uid === data.uid);
      const hiddenBefore = chatlogData?.hiddenBefore?.[recipientUid] || null;
      if (senderMember) {
        await setDoc(recipientChatRef, {
          contactUid: data.uid,
          chatId,
          name: senderMember.name,
          photoURL: senderMember.photoURL || '',
          contactJobTitle: senderMember.contactJobTitle || '',
          ...(hiddenBefore ? { clearedBefore: hiddenBefore } : {})
        });
      }
    }
  }

  // Write to subcollection — use server timestamp for accurate cross-client ordering
  await setDoc(doc(db, 'chatlogs', chatId, 'messages', data.messageId), {
    ...data,
    timestamp: serverTimestamp()
  });

  // Update parent doc: lastMessage preview + increment recipient unread counter
  // This triggers onSnapshot on recipient's side — chat entry already exists by now.
  const parentUpdate = {
    lastMessage: {
      message: data.message || '',
      timestamp: data.timestamp,
      uid: data.uid,
      messageId: data.messageId,
      ...(data.attachedImageUrl ? { attachedImageUrl: data.attachedImageUrl } : {})
    }
  };
  if (recipientUid) {
    parentUpdate[`unreadCounts.${recipientUid}`] = increment(1);
  }
  await updateDoc(doc(db, 'chatlogs', chatId), parentUpdate);

  return data;
});

// Kept for one-off reads (e.g. SellerProfile redirect). Active chat uses onSnapshot instead.
export const fetchMessages = createAsyncThunk('message/fetchMessages', async (chatId) => {
  const q = query(
    collection(db, 'chatlogs', chatId, 'messages'),
    orderBy('timestamp', 'desc'),
    limit(PAGE_SIZE)
  );
  const snap = await getDocs(q);
  const messages = snap.docs.map((d) => d.data()).reverse();
  const hasMore = snap.docs.length === PAGE_SIZE;
  const oldestTimestamp = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1].data().timestamp : null;
  return { messages, hasMore, oldestTimestamp };
});

export const fetchMoreMessages = createAsyncThunk(
  'message/fetchMoreMessages',
  async ({ chatId, oldestTimestamp }) => {
    const q = query(
      collection(db, 'chatlogs', chatId, 'messages'),
      orderBy('timestamp', 'desc'),
      startAfter(oldestTimestamp),
      limit(PAGE_SIZE + 1)
    );
    const snap = await getDocs(q);
    const hasMore = snap.docs.length > PAGE_SIZE;
    const docs = hasMore ? snap.docs.slice(0, PAGE_SIZE) : snap.docs;
    const messages = docs.map((d) => d.data()).reverse();
    const newOldest = docs.length > 0 ? docs[docs.length - 1].data().timestamp : null;
    return { messages, hasMore, oldestTimestamp: newOldest };
  }
);

export const fetchUserChats = createAsyncThunk('message/fetchUserChats', async (userId) => {
  try {
    const snap = await getDocs(collection(db, 'users', userId, 'chats'));
    return snap.docs.map((d) => d.data());
  } catch (error) {
    return error;
  }
});

// Reads lastMessage from parent doc (one per chat) — much lighter than fetching all messages
export const fetchUserMessages = createAsyncThunk('message/fetchUserMessages', async (userId, { rejectWithValue }) => {
  try {
    const snap = await getDocs(
      query(collection(db, 'chatlogs'), where('memberUids', 'array-contains', userId))
    );
    return snap.docs.map((chatDoc) => ({
      chatId: chatDoc.id,
      messages: chatDoc.data().lastMessage ? [chatDoc.data().lastMessage] : []
    }));
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

// Reads unreadCounts field from parent doc — no subcollection queries needed
export const findUnreadSenders = createAsyncThunk('message/findUnreadSenders', async (currentUserId) => {
  try {
    const snap = await getDocs(
      query(collection(db, 'chatlogs'), where('memberUids', 'array-contains', currentUserId))
    );
    const senderInfo = [];
    snap.docs.forEach((chatDoc) => {
      const data = chatDoc.data();
      const unreadCount = data.unreadCounts?.[currentUserId] || 0;
      if (unreadCount > 0) {
        const contactInfo = data.members?.find((m) => m.uid !== currentUserId);
        if (contactInfo) {
          senderInfo.push({
            chatId: chatDoc.id,
            contactId: contactInfo.uid,
            contactName: contactInfo.name,
            contactPhotoURL: contactInfo.photoURL,
            unreadCount
          });
        }
      }
    });
    return senderInfo;
  } catch (error) {
    throw error;
  }
});

export const markMessagesAsRead = createAsyncThunk(
  'message/markMessagesAsRead',
  async ({ chatId, currentUserId }) => {
    // Reset counter on parent doc immediately
    await updateDoc(doc(db, 'chatlogs', chatId), {
      [`unreadCounts.${currentUserId}`]: 0
    });

    // Batch-mark unread messages from others as read
    const snap = await getDocs(
      query(collection(db, 'chatlogs', chatId, 'messages'), where('read', '==', false))
    );
    const toUpdate = snap.docs.filter((d) => d.data().uid !== currentUserId);
    if (toUpdate.length === 0) return [];

    const batch = writeBatch(db);
    toUpdate.forEach((d) => batch.update(d.ref, { read: true }));
    await batch.commit();

    return toUpdate.map((d) => d.data().messageId);
  }
);

const initialState = {
  latestMessages: [],    // last PAGE_SIZE messages, kept in sync by onSnapshot in Messages.jsx
  olderMessages: [],     // older messages prepended on scroll-up pagination
  userMessages: [],      // last message per chat (for contact list preview)
  userChats: [],
  unreadContacts: [],
  readByMap: {},         // { [chatId]: { [uid]: seconds } } — from Firestore readBy field
  contactsListenerReady: false, // true after useContactsListener delivers its first snapshot
  hasMore: false,
  loadingMore: false,
  oldestTimestamp: null  // Firestore cursor for next fetchMoreMessages call
};

const updateUserMessageReducer = (state, action) => {
  const { chatId, messages } = action.payload;
  const index = state.userMessages.findIndex((m) => m.chatId === chatId);
  if (index !== -1) {
    state.userMessages[index].messages = messages;
  } else {
    state.userMessages.push({ chatId, messages });
  }
};

export const messageSlice = createSlice({
  name: 'message',
  initialState,
  reducers: {
    resetMessage() {
      return initialState;
    },
    clearMessages(state) {
      state.latestMessages = [];
      state.olderMessages = [];
      state.hasMore = false;
      state.loadingMore = false;
      state.oldestTimestamp = null;
    },
    // Called by the onSnapshot listener in Messages.jsx whenever messages change
    setLatestMessages(state, action) {
      state.latestMessages = action.payload.messages;
      // Only update the pagination cursor on the very first snapshot (before any fetchMoreMessages)
      if (state.olderMessages.length === 0) {
        state.hasMore = action.payload.hasMore;
        state.oldestTimestamp = action.payload.oldestTimestamp;
      }
    },
    updateUserMessage: updateUserMessageReducer,
    // Synchronous update for a single chat's unread count — called from the query listener
    // instead of dispatching findUnreadSenders (which does an extra round-trip for data we already have)
    updateUnreadContact(state, action) {
      const { chatId, contactId, unreadCount } = action.payload;
      const idx = state.unreadContacts.findIndex((uc) => uc.chatId === chatId);
      if (unreadCount === 0) {
        if (idx !== -1) state.unreadContacts.splice(idx, 1);
      } else {
        const entry = { chatId, contactId, unreadCount };
        if (idx !== -1) state.unreadContacts[idx] = entry;
        else state.unreadContacts.push(entry);
      }
    },
    // Called by useContactsListener whenever a chatlog doc changes — stores readBy as plain seconds
    setReadByForChat(state, action) {
      const { chatId, readBy } = action.payload;
      if (!state.readByMap) state.readByMap = {};
      state.readByMap[chatId] = readBy;
    },
    setContactsListenerReady(state) {
      state.contactsListenerReady = true;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(addMessage.fulfilled, () => {
        // Message appears via onSnapshot — no manual state update needed
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.latestMessages = action.payload.messages;
        state.olderMessages = [];
        state.hasMore = action.payload.hasMore;
        state.oldestTimestamp = action.payload.oldestTimestamp;
        state.loadingMore = false;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchMoreMessages.pending, (state) => {
        state.loadingMore = true;
      })
      .addCase(fetchMoreMessages.fulfilled, (state, action) => {
        state.olderMessages = [...action.payload.messages, ...state.olderMessages];
        state.hasMore = action.payload.hasMore;
        state.oldestTimestamp = action.payload.oldestTimestamp;
        state.loadingMore = false;
      })
      .addCase(fetchMoreMessages.rejected, (state) => {
        state.loadingMore = false;
      })
      .addCase(fetchUserMessages.fulfilled, (state, action) => {
        state.userMessages = action.payload;
      })
      .addCase(fetchUserChats.fulfilled, (state, action) => {
        state.userChats = action.payload;
      })
      .addCase(markMessagesAsRead.fulfilled, (state, action) => {
        const readIds = new Set(action.payload);
        state.latestMessages = state.latestMessages.map((m) =>
          readIds.has(m.messageId) ? { ...m, read: true } : m
        );
        state.olderMessages = state.olderMessages.map((m) =>
          readIds.has(m.messageId) ? { ...m, read: true } : m
        );
      })
      .addCase(findUnreadSenders.fulfilled, (state, action) => {
        state.unreadContacts = action.payload;
      });
  }
});

export const { resetMessage, clearMessages, setLatestMessages, updateUserMessage, updateUnreadContact, setReadByForChat, setContactsListenerReady } =
  messageSlice.actions;
export default messageSlice.reducer;
